import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';
import {
    Box,
    Button,
    Collapse,
    FormHelperText,
    Grid,
    IconButton,
    Stack,
    SxProps,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { Fragment, useEffect, useState, createContext, useContext, type JSX } from 'react';
import { useAtom } from 'ximple';
import { useAtomReducer } from 'src/hooks/useAtomReducer';
import { useLocale, getLocaleNumberFormatTwoDecimals } from '@repo/i18n';
import { BilberryPromoCodeStatus } from '@repo/types';
import { voucherAtom, setPromoCode, resetPromoCode } from 'src/state/voucher/voucherAtom';
import { useCustomizations } from 'src/components/utils/theme/customizations';
import TypographyStyleUtils from 'src/components/utils/jss/TypographyStyleUtils';
import { usePromocodeStatus } from '@repo/widget-utils/services/hooks/product';
import { CancelRounded } from '@mui/icons-material';
import { useConfigurations } from '@repo/widget-utils/widgetsConfiguration';

// The purpose of this context is to make sure `globalPromoCode` is only
// applied once upon mounting the `SummaryPromoCodeWritable` component.
//
// We need this to prevent the component from re-applying the
// `globalPromoCode`, effectively preventing you from resetting it, in
// cases where an enduser wants to use a different promocode.
const SummaryPromoCodeContext = createContext({
    dontReapplyGlobal: false,
    setDontReapplyGlobal: (() => {}) as (_: boolean) => void,
});

export function SummaryPromoCodeProvider({ children }: { children: React.ReactNode }) {
    const [dontReapplyGlobal, setDontReapplyGlobal] = useState(false);

    return (
        <SummaryPromoCodeContext.Provider value={{ dontReapplyGlobal, setDontReapplyGlobal }}>
            {children}
        </SummaryPromoCodeContext.Provider>
    );
}

interface Props {
    sx?: SxProps;
    // Match the canonical type in Summary.tsx — a plain CSS-property object.
    // SxProps is too wide here: spreading a union that includes arrays/null
    // into an object literal (line 279) and placing it inside an `sx` array
    // (line 296) both trip TS overload resolution.
    invertedColorsClass?: Record<string, unknown>;
    writeable?: boolean;
    currentPriceReduction: number;
    // If promocode  must be compatible with giftcard cart-items.
    compatibleWithGiftcards?: boolean;
}

export default function SummaryPromoCode(props: Props): JSX.Element | null {
    const [{ appliedPromoCode }] = useAtom(voucherAtom);

    if (!props.writeable) {
        return appliedPromoCode ? (
            <PromoCodeStatusDisplay
                sx={props.sx}
                promoCodeStatus={appliedPromoCode}
                currentPriceReduction={props.currentPriceReduction}
                invertedColorsClass={props.invertedColorsClass}
            />
        ) : null;
    }

    return <SummaryPromoCodeWriteable {...props} />;
}

function SummaryPromoCodeWriteable(props: Props): JSX.Element {
    const customizations = useCustomizations();
    const theme = useTheme();
    const configuration = useConfigurations();
    const [{ promoCode, appliedPromoCode }, voucherDispatch] = useAtomReducer(voucherAtom);
    const [expanded, setExpanded] = useState(!!appliedPromoCode);
    const { t } = useLocale();
    const [inputValue, setInputValue] = useState(promoCode);
    const { dontReapplyGlobal, setDontReapplyGlobal } = useContext(SummaryPromoCodeContext);
    const [tryPromoCode, setTryPromoCode] = useState<string | undefined>(
        // Set to globalPromoCode if writable and no promoCode is active.
        props.writeable && !dontReapplyGlobal ? configuration.globalPromoCode : undefined,
    );
    const [errorMessage, setErrorMessage] = useState<string | undefined>('');
    const { data, isLoading, error } = usePromocodeStatus(tryPromoCode);

    // Set to true after initial render, to prevent `globalPromoCode` from being reapplied
    // more than once.
    useEffect(() => {
        if (promoCode === configuration.globalPromoCode) setDontReapplyGlobal(true);
    }, [promoCode, configuration.globalPromoCode, setDontReapplyGlobal]);

    useEffect(() => {
        // If a promocode has been set, expand the rendered view.
        if (appliedPromoCode) setExpanded(true);

        // If no promocode is set and not currently loading, reset.
        if (!appliedPromoCode && !isLoading) setTryPromoCode(undefined);
    }, [appliedPromoCode, promoCode, isLoading]);

    // React to swr-hook results for fetching promo-code.
    useEffect(() => {
        // If `props.compatibleWithGiftcards` is set,
        // check if promocode is compatible with
        // giftcards.
        const notGiftcardCompatible = data && props.compatibleWithGiftcards && !data.for_giftcard;

        if (error || notGiftcardCompatible) {
            // Only display error and reset input if
            // `inputValue` is not empty.
            // In cases where a global promocode is
            // set, we don't want to display an error
            // if that code is invalid, since it was
            // not typed manually by the user.
            if (inputValue !== '') {
                setErrorMessage(
                    notGiftcardCompatible
                        ? t.promo_code_not_supported_for_gift_cards
                        : t.couldnt_apply_promocode_is_the_code_correct,
                );
                setInputValue('');
            }
        } else if (data && tryPromoCode) {
            voucherDispatch(setPromoCode(data));
            setInputValue(tryPromoCode);
        }
    }, [
        data,
        error,
        tryPromoCode,
        inputValue,
        voucherDispatch,
        props.compatibleWithGiftcards,
        t.couldnt_apply_promocode_is_the_code_correct,
        t.promo_code_not_supported_for_gift_cards,
    ]);

    return (
        <Box sx={props.sx} borderBottom={`1px solid ${theme.palette.grey[400]}`}>
            <Grid container alignItems="center">
                <Button
                    color="primary"
                    onClick={() => setExpanded(!expanded)}
                    aria-expanded={expanded}
                    aria-label={
                        !expanded ? t.aria_show_promocode_input : t.aria_hide_promocode_input
                    }
                    sx={{
                        '& .MuiSvgIcon-root': {
                            color: customizations.linkColor,
                        },
                        color: customizations.linkColor,
                        px: 0,
                        py: 1,
                    }}
                    endIcon={
                        <KeyboardArrowDownRounded
                            sx={{
                                transition: theme.transitions.create('transform', {
                                    duration: theme.transitions.duration.standard,
                                }),
                                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            }}
                        />
                    }
                >
                    {t.enter_promo_code}
                </Button>
            </Grid>
            <Collapse
                in={expanded}
                sx={{ paddingBottom: theme.spacing(2) }}
                timeout="auto"
                unmountOnExit
            >
                <Fragment>
                    <Stack direction="row" flexWrap="nowrap" justifyContent="space-between">
                        <Stack>
                            <TextField
                                error={!!errorMessage}
                                sx={{
                                    backgroundColor: theme.palette.background.paper,
                                    marginRight: theme.spacing(1),
                                    width: '264px',
                                    [`@media screen and (max-width: ${768}px)`]: {
                                        width: '232px',
                                    },
                                }}
                                onChange={(e) => {
                                    setInputValue(e.currentTarget.value);
                                    setErrorMessage(undefined);
                                    setTryPromoCode(undefined);
                                }}
                                onKeyDown={(ev: React.KeyboardEvent) => {
                                    if (ev.code?.toLowerCase() === 'enter') {
                                        setTryPromoCode(inputValue);
                                    }
                                }}
                                variant="outlined"
                                value={inputValue}
                                color="secondary"
                                placeholder={t.fill_promocode}
                                disabled={appliedPromoCode !== null || isLoading}
                                size="small"
                            />
                            {errorMessage && (
                                <FormHelperText sx={{ color: theme.palette.error.main }}>
                                    {errorMessage}
                                </FormHelperText>
                            )}
                        </Stack>
                        <Button
                            variant="outlined"
                            sx={{
                                borderRadius: theme.spacing(1),
                                borderColor: theme.palette.primary.main,
                                height: theme.spacing(5),
                            }}
                            onClick={() => {
                                setTryPromoCode(inputValue);
                            }}
                            aria-label={t.enter_promo_code}
                        >
                            {t.apply.toUpperCase()}
                        </Button>
                    </Stack>
                </Fragment>
            </Collapse>
            {appliedPromoCode && (
                <PromoCodeStatusDisplay
                    invertedColorsClass={props.invertedColorsClass}
                    promoCodeStatus={appliedPromoCode}
                    currentPriceReduction={props.currentPriceReduction}
                    onClear={() => {
                        setTryPromoCode(undefined);
                        setInputValue('');
                        voucherDispatch(resetPromoCode());
                    }}
                    writable={props.writeable}
                />
            )}
        </Box>
    );
}

function PromoCodeStatusDisplay({
    sx,
    invertedColorsClass,
    promoCodeStatus,
    currentPriceReduction,
    onClear,
    writable,
}: {
    sx?: SxProps;
    invertedColorsClass?: Record<string, unknown>;
    promoCodeStatus: BilberryPromoCodeStatus;
    currentPriceReduction: number;
    onClear?: () => void;
    writable?: boolean;
}) {
    const { locale, t } = useLocale();
    const theme = useTheme();

    return (
        <Stack sx={sx}>
            {currentPriceReduction === 0 && (
                <Typography fontSize="75%" sx={{ ...invertedColorsClass, fontStyle: 'italic' }}>
                    {t.promocodeIsNotCompatibleWithCart}
                </Typography>
            )}
            <Stack direction="row" flexWrap="nowrap" alignItems="center">
                <Typography
                    align="right"
                    sx={[
                        TypographyStyleUtils.secondaryTextColorSx,
                        { flex: 3, ...invertedColorsClass },
                    ]}
                >
                    {promoCodeStatus?.coupon_code}
                </Typography>

                <Stack
                    direction="row"
                    justifyContent="flex-end"
                    flexWrap="nowrap"
                    gap={2}
                    alignItems="center"
                    flex={1}
                >
                    <Typography
                        align="right"
                        pl={2}
                        sx={[TypographyStyleUtils.secondaryTextColorSx, invertedColorsClass ?? {}]}
                    >
                        -{getLocaleNumberFormatTwoDecimals(locale, currentPriceReduction)}
                    </Typography>
                    {onClear && writable && (
                        <IconButton
                            aria-label={`${t.remove} ${promoCodeStatus?.coupon_code}`}
                            sx={{
                                alignSelf: 'flex-start',
                                padding: 0,
                                '& .MuiSvgIcon-root': {
                                    color: theme.palette.common.black,
                                },
                                color: theme.palette.common.black,
                            }}
                            onClick={onClear}
                        >
                            <CancelRounded fontSize="small" />
                        </IconButton>
                    )}
                </Stack>
            </Stack>
        </Stack>
    );
}
